import { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import DoctorProtocolEditor from './DoctorProtocolEditor';

const DEFAULT_MEDICATION_GROUPS = [
  { options: [], default: null },
];

export default function AdminDoctorsPage() {
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState('');
  const [staff, setStaff] = useState([]);
  const [laxatives, setLaxatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchHospitals();
    fetchStaff();
    fetchLaxatives();
  }, []);

  useEffect(() => {
    fetchDoctors();
  }, [selectedHospitalId]);

  const fetchHospitals = async () => {
    try {
      const { data } = await axios.get('/api/admin/hospitals');
      if (data.success) {
        setHospitals(data.data || []);
        if (data.data?.length) {
          setSelectedHospitalId(data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch hospitals:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const { data } = await axios.get('/api/admin/staff');
      if (data.success) setStaff(data.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const fetchLaxatives = async () => {
    try {
      const { data } = await axios.get('/api/admin/laxatives');
      if (data.success) setLaxatives(data.data);
    } catch (error) {
      console.error('Failed to fetch laxatives:', error);
    }
  };

  const fetchDoctors = async () => {
    if (!selectedHospitalId) {
      setDoctors([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/admin/doctors?hospitalId=${selectedHospitalId}`);
      if (data.success) setDoctors(data.data);
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditing({
      name: '',
      locations: [],
      defaultLocation: '',
      operatorId: '',
      isActive: true,
      hospitalId: selectedHospitalId,
      medicationGroups: DEFAULT_MEDICATION_GROUPS.map(g => ({ ...g, options: [...g.options] })),
    });
    setModalOpen(true);
  };

  const handleEdit = (doctor) => {
    const hospital = hospitals.find(h => h.id === doctor.hospitalId);
    const facilityLocations = hospital?.locations || [];
    // A doctor can only be assigned locations that exist in his facility's location list.
    const doctorLocations = (doctor.locations || [])
      .filter(loc => facilityLocations.includes(loc));
    const validDefault = facilityLocations.includes(doctor.defaultLocation)
      ? doctor.defaultLocation
      : (facilityLocations.includes(doctor.location) ? doctor.location : (doctorLocations[0] || ''));
    setEditing({
      ...doctor,
      locations: doctorLocations,
      defaultLocation: validDefault,
      hospitalId: doctor.hospitalId || '',
      hospital: doctor.Hospital,
      operatorId: doctor.operatorId || '',
      operator: doctor.Operator,
      medicationGroups: (doctor.medicationGroups || []).map(g => ({
        options: [...(g.options || [])],
        default: g.default || null,
      })),
    });
    setModalOpen(true);
  };

  const handleAddGroup = () => {
    setEditing({
      ...editing,
      medicationGroups: [...editing.medicationGroups, { options: [], default: null }],
    });
  };

  const handleRemoveGroup = (groupIdx) => {
    setEditing({
      ...editing,
      medicationGroups: editing.medicationGroups.filter((_, idx) => idx !== groupIdx),
    });
  };

  const handleToggleLaxative = (groupIdx, laxativeName) => {
    const groups = editing.medicationGroups.map((group, idx) => {
      if (idx !== groupIdx) return group;
      const isSelected = group.options.includes(laxativeName);
      const options = isSelected
        ? group.options.filter(o => o !== laxativeName)
        : [...group.options, laxativeName];
      const nextDefault = isSelected && group.default === laxativeName ? null : group.default;
      return { ...group, options, default: nextDefault };
    });
    setEditing({ ...editing, medicationGroups: groups });
  };

  const handleGroupDefaultChange = (groupIdx, value) => {
    const groups = editing.medicationGroups.map((group, idx) =>
      idx === groupIdx ? { ...group, default: value || null } : group
    );
    setEditing({ ...editing, medicationGroups: groups });
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this doctor?')) return;
    try {
      await axios.delete(`/api/admin/doctors/${id}`);
      fetchDoctors();
    } catch (error) {
      console.error('Failed to delete doctor:', error);
    }
  };

  const handleSave = async () => {
    const medicationGroups = editing.medicationGroups.filter(g => g.options.length > 0);
    const facility = hospitals.find(h => h.id === editing.hospitalId);
    const facilityLocations = facility?.locations || [];
    const validLocations = (editing.locations || [])
      .filter(loc => facilityLocations.includes(loc));
    const validDefault = facilityLocations.includes(editing.defaultLocation)
      ? editing.defaultLocation
      : (validLocations[0] || null);

    const payload = {
      name: editing.name,
      locations: validLocations,
      defaultLocation: validDefault,
      hospitalId: editing.hospitalId || null,
      operatorId: editing.operatorId || null,
      isActive: editing.isActive,
      medicationGroups,
    };

    try {
      if (editing.id) {
        await axios.put(`/api/admin/doctors/${editing.id}`, payload);
      } else {
        await axios.post('/api/admin/doctors', payload);
      }
      setModalOpen(false);
      setEditing(null);
      fetchDoctors();
    } catch (error) {
      console.error('Failed to save doctor:', error);
      alert('Failed to save doctor: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) return <Loading>Loading...</Loading>;

  const activeHospital = hospitals.find(h => h.id === (editing?.hospitalId || selectedHospitalId));
  const hospitalLocationOptions = activeHospital?.locations || [];

  return (
    <Container>
      <Header>
        <h1>Doctors</h1>
        <div>
          <HospitalSelect
            value={selectedHospitalId}
            onChange={e => setSelectedHospitalId(e.target.value)}
          >
            {hospitals.map(h => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </HospitalSelect>
          <AddButton onClick={handleCreate} style={{ marginLeft: '1rem' }}>+ Add Doctor</AddButton>
        </div>
      </Header>

      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Facility</Th>
            <Th>Location</Th>
            <Th>Hospital</Th>
            <Th>Operator</Th>
            <Th>Active</Th>
            <Th>Default Groups</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {doctors.map(doctor => (
            <Tr key={doctor.id}>
              <Td>{doctor.name}</Td>
              <Td>{hospitals.find(h => h.id === doctor.hospitalId)?.name || '—'}</Td>
              <Td>
              {(doctor.locations || []).length
                ? `${doctor.locations.join(', ')}${doctor.defaultLocation ? ` (default: ${doctor.defaultLocation})` : ''}`
                : '—'}
            </Td>
              <Td>{doctor.Hospital?.name || '—'}</Td>
              <Td>{doctor.Operator?.name || '—'}</Td>
              <Td>{doctor.isActive ? 'Yes' : 'No'}</Td>
              <Td>{(doctor.medicationGroups || []).map(g => g.default).filter(Boolean).join(', ') || '—'}</Td>
              <Td align="right">
                <ActionGroup>
                  <TableButton onClick={() => handleEdit(doctor)}>Edit</TableButton>
                  <TableButton danger onClick={() => handleDelete(doctor.id)}>Delete</TableButton>
                </ActionGroup>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {doctors.length === 0 && <EmptyState>No doctors found.</EmptyState>}

      {modalOpen && editing && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editing.id ? 'Edit Doctor' : 'Add Doctor'}</h2>
              <CloseButton onClick={() => setModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label>Facility</label>
                <Select
                  value={editing.hospitalId || ''}
                  onChange={e => setEditing({ ...editing, hospitalId: e.target.value })}
                >
                  <option value="">— none —</option>
                  {hospitals.map(h => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </Select>
              </FormField>
              <FormField>
                <label>Name</label>
                <Input
                  value={editing.name || ''}
                  onChange={e => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Dr. FirstName LastName"
                />
              </FormField>
              <FormField>
                <label>Locations</label>
                <Hint>Check the clinic locations this doctor uses, then pick the default.</Hint>
                {hospitalLocationOptions.length === 0 && (
                  <Hint>No clinic locations configured yet. Add locations in the facility page first.</Hint>
                )}
                <CheckboxList>
                  {hospitalLocationOptions.map(location => {
                    const isSelected = editing.locations.includes(location);
                    return (
                      <CheckboxLabel key={location}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const nextLocations = isSelected
                              ? editing.locations.filter(l => l !== location)
                              : [...editing.locations, location];
                            setEditing({
                              ...editing,
                              locations: nextLocations,
                              defaultLocation: editing.defaultLocation === location && isSelected
                                ? (nextLocations[0] || '')
                                : editing.defaultLocation,
                            });
                          }}
                        />
                        {location}
                        {isSelected && (
                          <input
                            type="radio"
                            name="defaultLocation"
                            checked={editing.defaultLocation === location}
                            onChange={() => setEditing({ ...editing, defaultLocation: location })}
                            style={{ marginLeft: '0.5rem' }}
                          />
                        )}
                      </CheckboxLabel>
                    );
                  })}
                </CheckboxList>
              </FormField>
              <FormField>
                <label>Hospital / Clinic</label>
                <Select
                  value={editing.hospitalId || ''}
                  onChange={e => setEditing({ ...editing, hospitalId: e.target.value })}
                >
                  <option value="">None / Global</option>
                  {hospitals.map(h => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </Select>
              </FormField>
              <FormField>
                <label>Operator</label>
                <Select
                  value={editing.operatorId || ''}
                  onChange={e => setEditing({ ...editing, operatorId: e.target.value })}
                >
                  <option value="">None</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </Select>
              </FormField>
              <FormField inline>
                <input
                  type="checkbox"
                  checked={editing.isActive}
                  onChange={e => setEditing({ ...editing, isActive: e.target.checked })}
                />
                <label>Active</label>
              </FormField>
              <FormField>
                <label>Laxative Groups</label>
                <Hint>
                  Each group is a category of interchangeable laxatives. Check the laxatives
                  allowed for this doctor in each group, then pick the default.
                </Hint>
                {editing.medicationGroups.map((group, groupIdx) => (
                  <GroupBox key={groupIdx}>
                    <GroupHeader>
                      <span>Group {groupIdx + 1}</span>
                      <TableButton danger onClick={() => handleRemoveGroup(groupIdx)}>Remove</TableButton>
                    </GroupHeader>
                    <CheckboxList>
                      {laxatives.map(laxative => (
                        <CheckboxLabel key={laxative.id}>
                          <input
                            type="checkbox"
                            checked={group.options.includes(laxative.name)}
                            onChange={() => handleToggleLaxative(groupIdx, laxative.name)}
                          />
                          {laxative.name}
                        </CheckboxLabel>
                      ))}
                      {laxatives.length === 0 && <Hint>No laxatives defined yet. Add some in the Laxatives page.</Hint>}
                    </CheckboxList>
                    <FormField>
                      <label>Default</label>
                      <Select
                        value={group.default || ''}
                        onChange={e => handleGroupDefaultChange(groupIdx, e.target.value)}
                      >
                        <option value="">None</option>
                        {group.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </Select>
                    </FormField>
                  </GroupBox>
                ))}
                <SecondaryButton type="button" onClick={handleAddGroup}>+ Add Group</SecondaryButton>
              </FormField>
              <DoctorProtocolEditor
                doctor={editing}
                onUpdated={(doctor) => {
                  setEditing((current) => ({ ...current, instructionProtocols: doctor.instructionProtocols }));
                  fetchDoctors();
                }}
              />
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => setModalOpen(false)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h1 {
    margin: 0;
    font-size: 1.5rem;
  }
`;

const AddButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f02a4c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #d12442;
  }
`;

const Loading = styled.div`
  padding: 2rem;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #666;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: ${props => props.align || 'left'};
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 0.85rem;
  color: #555;
  border-bottom: 1px solid #e9ecef;
`;

const Tr = styled.tr`
  border-bottom: 1px solid #e9ecef;
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  text-align: ${props => props.align || 'left'};
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const TableButton = styled.button`
  padding: 0.35rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  background: ${props => props.danger ? '#fee2e2' : '#e0e7ff'};
  color: ${props => props.danger ? '#991b1b' : '#3730a3'};

  &:hover {
    background: ${props => props.danger ? '#fecaca' : '#c7d2fe'};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;

  h2 {
    margin: 0;
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: ${props => props.inline ? 'inline' : 'block'};
    margin-left: ${props => props.inline ? '0.5rem' : '0'};
    margin-bottom: ${props => props.inline ? '0' : '0.35rem'};
    font-weight: 600;
    font-size: 0.85rem;
    color: #444;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  box-sizing: border-box;
  background: white;
`;

const HospitalSelect = styled(Select)`
  min-width: 220px;
`;

const GroupBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: #fafafa;
`;

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.85rem;
  color: #444;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 0.75rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 400;
  color: #333;
`;

const Hint = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #666;

  code {
    background: #f3f4f6;
    padding: 0.1rem 0.3rem;
    border-radius: 4px;
  }
`;

const PrimaryButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f02a4c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #d12442;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #e5e7eb;
  }
`;
